import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useActivateAccount } from '../services/auth/auth';
import { useAlert } from '../context/AlertContext';
import { useAuthStore } from '../store/authStore';
import { Loader2 } from 'lucide-react';

const ActivateAccount = () => {
    const { code } = useParams();
    const navigate = useNavigate();
    const { mutateAsync: activate } = useActivateAccount();
    const setAlert = useAlert();
    const { setTokens } = useAuthStore();
    const [status, setStatus] = useState('activating'); // 'activating', 'success', 'error'
    const hasAttempted = useRef(false);

    useEffect(() => {
        const activateUser = async () => {
            if (hasAttempted.current) return;
            hasAttempted.current = true;
            
            try {
                const response = await activate({ code });
                
                setStatus('success');
                setAlert({ type: 'success', message: response.message || 'Account activated successfully! Please sign in.' });
                
                setTimeout(() => {
                    navigate('/clients/signin');
                }, 2000);
            } catch (error) {
                setStatus('error');
                const err = error.response?.data?.error || 'Failed to activate account. The code may be invalid or expired.';
                setAlert({ type: 'error', message: err });
            }
        };

        if (code) {
            activateUser();
        } else {
            setStatus('error');
            setAlert({ type: 'error', message: 'No activation code provided in the URL.' });
        }
    }, [code, activate, navigate, setAlert, setTokens]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 font-['Poppins']">
            <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                <h1 className="text-2xl font-bold text-[#174568] mb-6">Account Activation</h1>
                
                {status === 'activating' && (
                    <div className="flex flex-col items-center">
                        <Loader2 className="animate-spin text-[#174568] mb-4" size={48} />
                   
                    </div>
                )}
                
                {status === 'success' && (
                    <div className="text-center">
                        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                        </div>
                        <p className="text-gray-800 font-medium mb-2">Activation Successful!</p>
                        <p className="text-gray-600 text-sm">Redirecting you to log in...</p>
                    </div>
                )}
                
                {status === 'error' && (
                    <div className="text-center">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </div>
                        <p className="text-gray-800 font-medium mb-4">Activation Failed</p>
                        <button 
                            onClick={() => navigate('/resend-activatation-link')}
                            className="w-full bg-[#174568] hover:bg-gray-800 text-white font-medium py-3 px-4 rounded transition-colors"
                        >
                            Request New Activation Link
                        </button>
                        <button 
                            onClick={() => navigate('/clients/signin')}
                            className="w-full mt-3 bg-white border border-[#174568] text-[#174568] hover:bg-gray-50 font-medium py-3 px-4 rounded transition-colors"
                        >
                            Back to Sign In
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivateAccount;
